import { Deal, TeamMember, Stage, Note } from '../types/pipeline'

export const STAGES: Stage[] = [
  'Pre-qual',
  'Application',
  'Processing',
  'Underwriting',
  'Clear to Close',
]

export const TEAM_MEMBERS: TeamMember[] = [
  {
    id: 'tm1',
    name: 'Sarah Chen',
    role: 'Senior Loan Officer',
    avatar:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  },
  {
    id: 'tm2',
    name: 'Michael Rodriguez',
    role: 'Loan Officer',
    avatar:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  },
  {
    id: 'tm3',
    name: 'Jennifer Kim',
    role: 'Loan Officer',
    avatar:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  },
  {
    id: 'tm4',
    name: 'David Thompson',
    role: 'Junior Loan Officer',
    avatar:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  },
]

// Helper to generate notes
const generateNotes = (dealId: string, loanOfficer: string): Note[] => {
  const notes: Note[] = []
  const now = new Date()

  // Sample notes based on deal
  const noteTemplates = [
    {
      content:
        'Borrower submitted all required income documentation. Credit report looks strong.',
      type: 'document' as const,
      daysAgo: 1,
    },
    {
      content:
        'Called borrower to discuss rate lock options. They want to wait until next week.',
      type: 'communication' as const,
      daysAgo: 2,
    },
    {
      content: 'Appraisal ordered. Expected completion in 5-7 business days.',
      type: 'note' as const,
      daysAgo: 3,
    },
    {
      content: 'Moved to Processing stage. All conditions cleared.',
      type: 'status_change' as const,
      daysAgo: 5,
    },
    {
      content:
        'Initial pre-qualification completed. DTI ratio at 38%, well within guidelines.',
      type: 'note' as const,
      daysAgo: 7,
    },
  ]

  const officer =
    TEAM_MEMBERS.find((m) => m.id === loanOfficer) || TEAM_MEMBERS[0]

  noteTemplates
    .slice(0, 3 + Math.floor(Math.random() * 2))
    .forEach((template, index) => {
      const noteDate = new Date(now)
      noteDate.setDate(noteDate.getDate() - template.daysAgo)

      notes.push({
        id: `${dealId}-note-${index}`,
        author: officer.name,
        authorAvatar: officer.avatar,
        content: template.content,
        timestamp: noteDate,
        type: template.type,
      })
    })

  return notes.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
}

export const MOCK_DEALS: Deal[] = [
  // Sarah Chen
  {
    id: 'd1',
    borrowerName: 'James Wilson',
    borrowerEmail: 'james.wilson@email.com',
    borrowerPhone: '(555) 123-4567',
    amount: 450000,
    stage: 'Pre-qual',
    daysInStage: 2,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '123 Oak Street, San Francisco, CA 94102',
    assignedTo: 'tm1',
    probability: 30,
    applicationDate: new Date('2024-01-15'),
    estimatedCloseDate: new Date('2024-03-15'),
    notes: generateNotes('d1', 'tm1'),
  },
  {
    id: 'd2',
    borrowerName: 'Elena Rodriguez',
    borrowerEmail: 'elena.r@email.com',
    borrowerPhone: '(555) 234-5678',
    amount: 320000,
    stage: 'Processing',
    daysInStage: 12,
    loanType: 'Refinance',
    propertyType: 'Condo',
    propertyAddress: '456 Pine Ave, Oakland, CA 94601',
    assignedTo: 'tm1',
    probability: 75,
    applicationDate: new Date('2024-01-05'),
    estimatedCloseDate: new Date('2024-02-28'),
    notes: generateNotes('d2', 'tm1'),
  },
  {
    id: 'd3',
    borrowerName: 'Marcus Johnson',
    borrowerEmail: 'marcus.j@email.com',
    borrowerPhone: '(555) 345-6789',
    amount: 650000,
    stage: 'Underwriting',
    daysInStage: 5,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '789 Maple Dr, Berkeley, CA 94704',
    assignedTo: 'tm1',
    probability: 90,
    applicationDate: new Date('2023-12-20'),
    estimatedCloseDate: new Date('2024-02-20'),
    notes: generateNotes('d3', 'tm1'),
  },
  {
    id: 'd4',
    borrowerName: 'Sophie Turner',
    borrowerEmail: 'sophie.turner@email.com',
    borrowerPhone: '(555) 456-7890',
    amount: 280000,
    stage: 'Clear to Close',
    daysInStage: 1,
    loanType: 'Purchase',
    propertyType: 'Townhouse',
    propertyAddress: '321 Elm Court, San Jose, CA 95110',
    assignedTo: 'tm1',
    probability: 98,
    applicationDate: new Date('2023-12-10'),
    estimatedCloseDate: new Date('2024-02-10'),
    notes: generateNotes('d4', 'tm1'),
  },
  {
    id: 'd5',
    borrowerName: 'Robert Chen',
    borrowerEmail: 'robert.chen@email.com',
    borrowerPhone: '(555) 567-8901',
    amount: 850000,
    stage: 'Application',
    daysInStage: 4,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '654 Birch Lane, Palo Alto, CA 94301',
    assignedTo: 'tm1',
    probability: 50,
    applicationDate: new Date('2024-01-10'),
    estimatedCloseDate: new Date('2024-03-25'),
    notes: generateNotes('d5', 'tm1'),
  },

  // Michael Rodriguez
  {
    id: 'd6',
    borrowerName: 'Lisa Wong',
    borrowerEmail: 'lisa.wong@email.com',
    borrowerPhone: '(555) 678-9012',
    amount: 550000,
    stage: 'Pre-qual',
    daysInStage: 5,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '987 Cedar St, Mountain View, CA 94040',
    assignedTo: 'tm2',
    probability: 30,
    applicationDate: new Date('2024-01-12'),
    estimatedCloseDate: new Date('2024-03-20'),
    notes: generateNotes('d6', 'tm2'),
  },
  {
    id: 'd7',
    borrowerName: 'David Miller',
    borrowerEmail: 'david.miller@email.com',
    borrowerPhone: '(555) 789-0123',
    amount: 420000,
    stage: 'Application',
    daysInStage: 8,
    loanType: 'Refinance',
    propertyType: 'Single Family',
    propertyAddress: '147 Willow Way, Sunnyvale, CA 94086',
    assignedTo: 'tm2',
    probability: 45,
    applicationDate: new Date('2024-01-08'),
    estimatedCloseDate: new Date('2024-03-10'),
    notes: generateNotes('d7', 'tm2'),
  },
  {
    id: 'd8',
    borrowerName: 'Sarah Palmer',
    borrowerEmail: 'sarah.palmer@email.com',
    borrowerPhone: '(555) 890-1234',
    amount: 380000,
    stage: 'Processing',
    daysInStage: 15,
    loanType: 'Cash-Out Refi',
    propertyType: 'Condo',
    propertyAddress: '258 Spruce Blvd, Fremont, CA 94538',
    assignedTo: 'tm2',
    probability: 70,
    applicationDate: new Date('2023-12-28'),
    estimatedCloseDate: new Date('2024-02-25'),
    notes: generateNotes('d8', 'tm2'),
  },
  {
    id: 'd9',
    borrowerName: "Kevin O'Connor",
    borrowerEmail: 'kevin.oconnor@email.com',
    borrowerPhone: '(555) 901-2345',
    amount: 720000,
    stage: 'Underwriting',
    daysInStage: 3,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '369 Redwood Ave, Santa Clara, CA 95050',
    assignedTo: 'tm2',
    probability: 85,
    applicationDate: new Date('2023-12-22'),
    estimatedCloseDate: new Date('2024-02-22'),
    notes: generateNotes('d9', 'tm2'),
  },

  // Jennifer Kim
  {
    id: 'd10',
    borrowerName: 'Amanda White',
    borrowerEmail: 'amanda.white@email.com',
    borrowerPhone: '(555) 012-3456',
    amount: 290000,
    stage: 'Pre-qual',
    daysInStage: 1,
    loanType: 'Purchase',
    propertyType: 'Townhouse',
    propertyAddress: '741 Ash Place, Milpitas, CA 95035',
    assignedTo: 'tm3',
    probability: 25,
    applicationDate: new Date('2024-01-18'),
    estimatedCloseDate: new Date('2024-03-30'),
    notes: generateNotes('d10', 'tm3'),
  },
  {
    id: 'd11',
    borrowerName: 'Tom Baker',
    borrowerEmail: 'tom.baker@email.com',
    borrowerPhone: '(555) 123-4568',
    amount: 620000,
    stage: 'Application',
    daysInStage: 6,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '852 Poplar Dr, Cupertino, CA 95014',
    assignedTo: 'tm3',
    probability: 55,
    applicationDate: new Date('2024-01-11'),
    estimatedCloseDate: new Date('2024-03-18'),
    notes: generateNotes('d11', 'tm3'),
  },
  {
    id: 'd12',
    borrowerName: 'Rachel Green',
    borrowerEmail: 'rachel.green@email.com',
    borrowerPhone: '(555) 234-5679',
    amount: 480000,
    stage: 'Processing',
    daysInStage: 10,
    loanType: 'Refinance',
    propertyType: 'Single Family',
    propertyAddress: '963 Hickory Ln, Los Gatos, CA 95032',
    assignedTo: 'tm3',
    probability: 80,
    applicationDate: new Date('2024-01-03'),
    estimatedCloseDate: new Date('2024-02-28'),
    notes: generateNotes('d12', 'tm3'),
  },
  {
    id: 'd13',
    borrowerName: 'Chris Stark',
    borrowerEmail: 'chris.stark@email.com',
    borrowerPhone: '(555) 345-6780',
    amount: 950000,
    stage: 'Clear to Close',
    daysInStage: 2,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '159 Magnolia St, Saratoga, CA 95070',
    assignedTo: 'tm3',
    probability: 99,
    applicationDate: new Date('2023-12-05'),
    estimatedCloseDate: new Date('2024-02-05'),
    notes: generateNotes('d13', 'tm3'),
  },
  {
    id: 'd14',
    borrowerName: 'Patty Mills',
    borrowerEmail: 'patty.mills@email.com',
    borrowerPhone: '(555) 456-7891',
    amount: 340000,
    stage: 'Processing',
    daysInStage: 18,
    loanType: 'Cash-Out Refi',
    propertyType: 'Condo',
    propertyAddress: '357 Sycamore Ct, Campbell, CA 95008',
    assignedTo: 'tm3',
    probability: 65,
    applicationDate: new Date('2023-12-25'),
    estimatedCloseDate: new Date('2024-02-20'),
    notes: generateNotes('d14', 'tm3'),
  },

  // David Thompson
  {
    id: 'd15',
    borrowerName: 'John Doe',
    borrowerEmail: 'john.doe@email.com',
    borrowerPhone: '(555) 567-8902',
    amount: 250000,
    stage: 'Pre-qual',
    daysInStage: 3,
    loanType: 'Purchase',
    propertyType: 'Condo',
    propertyAddress: '753 Walnut Ave, San Mateo, CA 94401',
    assignedTo: 'tm4',
    probability: 20,
    applicationDate: new Date('2024-01-16'),
    estimatedCloseDate: new Date('2024-04-01'),
    notes: generateNotes('d15', 'tm4'),
  },
  {
    id: 'd16',
    borrowerName: 'Jane Smith',
    borrowerEmail: 'jane.smith@email.com',
    borrowerPhone: '(555) 678-9013',
    amount: 310000,
    stage: 'Application',
    daysInStage: 7,
    loanType: 'Purchase',
    propertyType: 'Townhouse',
    propertyAddress: '951 Chestnut Rd, Redwood City, CA 94063',
    assignedTo: 'tm4',
    probability: 40,
    applicationDate: new Date('2024-01-09'),
    estimatedCloseDate: new Date('2024-03-15'),
    notes: generateNotes('d16', 'tm4'),
  },
  {
    id: 'd17',
    borrowerName: 'Mike Ross',
    borrowerEmail: 'mike.ross@email.com',
    borrowerPhone: '(555) 789-0124',
    amount: 580000,
    stage: 'Processing',
    daysInStage: 14,
    loanType: 'Purchase',
    propertyType: 'Single Family',
    propertyAddress: '486 Beech St, Foster City, CA 94404',
    assignedTo: 'tm4',
    probability: 75,
    applicationDate: new Date('2023-12-30'),
    estimatedCloseDate: new Date('2024-03-01'),
    notes: generateNotes('d17', 'tm4'),
  },
]
